import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useAtomValue } from "jotai";
import { Loader } from "lucide-react";
import { Button } from "@ui/Button";
import { stepDataStateAtom } from "@/state/formSession";
import type { StepKey } from "@/types/Form";
import { useIsMobile, useMultiStepForm } from "@/hooks";
import { cn } from "@/utils/tailwind";
import { hasValue } from "@/utils/validation";
import { StepCard } from "@/components/Cards";

export const FormContent: FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();
  const {
    currentStep,
    nextStep,
    previousStep,
    isFirstStep,
    isLastStep,
    form,
    currentStepIndex,
    isLoading,
  } = useMultiStepForm();
  const isMobile = useIsMobile();
  const stepDataState = useAtomValue(stepDataStateAtom);
  const stepKey = `step${(currentStepIndex + 1).toString()}` as StepKey;
  const hasStepValues = hasValue(form.getValues(stepKey));
  const isStepPersisted = stepDataState[stepKey] === "sent";
  const isDisabled = Boolean(isLoading) || (!hasStepValues && !isStepPersisted);

  return (
    <div className={cn("flex flex-col gap-8", className)}>
      <StepCard isLastStep={isLastStep} stepTitle={currentStep.title}>
        <currentStep.component />
      </StepCard>
      {!isLastStep && (
        <div className="mb-12 flex justify-between">
          {!isFirstStep && (
            <Button
              data-test="step-back-button"
              type="button"
              variant="secondary"
              className="uppercase"
              onClick={previousStep}
            >
              {t("back")}
            </Button>
          )}

          <Button
            data-test="step-next-button"
            disabled={isDisabled}
            type="button"
            onClick={() => void nextStep()}
            className={cn(
              "ml-auto max-w-[110px] uppercase lg:max-w-none",
              isMobile ? "w-full" : "min-w-[103px]",
            )}
          >
            {isLoading ? (
              <Loader className="animate-spin" />
            ) : isMobile ? (
              t("next")
            ) : (
              (currentStep.buttonText ?? t("next"))
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
